import React, { useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Grid,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useEmailField } from '../hooks/useEmailField';

import AuthorizedSignCheckbox from '../authorized-sign-checkbox/AuthorizedSignCheckbox';
import styles from './CreateSupplierContactModal.module.scss';
import { createSupplierContact } from '../../slices/suppliers/suppliersSlice';
import { SupplierContact } from '../../models/supplier.contact.model';
import { toast } from 'react-toastify';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { useErrorMessage } from '../hooks/useErrorMessage';

interface CreateContactModalProps {
  open: boolean;
  onClose: () => void;
  onSuccessfulSubmit?: () => void;
  supplierId: number;
  companyId: number;
}

export default function CreateSupplierContactModal({
  open,
  onClose,
  onSuccessfulSubmit,
  supplierId,
  companyId,
}: CreateContactModalProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLDivElement | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    email_address: '',
    phone_number: '',
    job_title: '',
    postal_address: '',
    authorized_rep: false,
    supplier_id: supplierId,
  });

  const handleChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  // Lower cases what is typed and applies the shared basic email validation.
  const emailField = useEmailField({
    value: formData.email_address,
    onChange: handleChange('email_address'),
    submitting: isSubmitting,
  });

  const handleCreate = () => {
    setIsSubmitting(true);
    dispatch(showOverlay());
    const errors = {
      full_name: !formData.full_name.trim(),
      // Optional field, but it has to be a valid address when filled in.
      email_address: !emailField.validate(),
    };

    if (Object.values(errors).some((error) => error)) {
      console.error('Required fields are empty or invalid');
      dispatch(hideOverlay());
      return;
    }
    dispatch(
      createSupplierContact({
        supplierId: supplierId,
        contact: formData as Omit<SupplierContact, 'id'>,
        company_id: companyId,
      })
    )
      .unwrap()
      .then(() => {
        if (onSuccessfulSubmit) {
          onSuccessfulSubmit();
        }
      })
      .catch((error) => toast.error(getErrorMessage(error)))
      .finally(() => {
        setIsSubmitting(false);
        dispatch(hideOverlay());
      });
  };

  return (
    <Modal open={open} onClose={onClose} className={'action-modal'} closeAfterTransition>
      <Grid component='div' container className={styles.modal_container}>
        <Grid component='div' size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
          <Card ref={formRef}>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant='h6' gutterBottom>
                {t('businessProfile.createContact')}
              </Typography>

              <Grid component='div' container spacing={2}>
                <Grid component='div' size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.fullName')} *`}
                    variant='outlined'
                    fullWidth
                    value={formData.full_name ?? ''}
                    onChange={(e) => handleChange('full_name')(e.target.value)}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12 }}>
                  <TextField
                    label={`${t('formInputs.emailAddressInputLabel')}`}
                    variant='outlined'
                    fullWidth
                    {...emailField.fieldProps}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.phoneNumber')}`}
                    variant='outlined'
                    type='tel'
                    fullWidth
                    value={formData.phone_number ?? ''}
                    onChange={(e) => handleChange('phone_number')(e.target.value)}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.jobTitle')}`}
                    variant='outlined'
                    fullWidth
                    value={formData.job_title ?? ''}
                    onChange={(e) => handleChange('job_title')(e.target.value)}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.postalAddress')}`}
                    variant='outlined'
                    fullWidth
                    value={formData.postal_address ?? ''}
                    onChange={(e) => handleChange('postal_address')(e.target.value)}
                  />
                </Grid>
              </Grid>

              <Grid component='div' container className={styles.checkbox_container}>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <AuthorizedSignCheckbox
                    authorizedRep
                    value={formData.authorized_rep}
                    onChange={handleChange('authorized_rep')}
                    disabled={false}
                  />
                </Grid>
              </Grid>
              <Stack
                component='div'
                direction='row'
                style={{
                  gap: '8px',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  marginTop: '16px',
                }}
              >
                <Button
                  id='createSupplierContactModalCloseBtn'
                  variant='contained'
                  className='btn-default btn-action-basic btn-cancel'
                  onClick={onClose}
                >
                  {t('buttons.close')}
                </Button>
                <Button
                  id='createSupplierContactModalCreateBtn'
                  variant='contained'
                  className='btn-default btn-action-basic'
                  onClick={handleCreate}
                >
                  {t('buttons.create')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
}
